<template>
	<!-- Available roles -->
	<div class="availability-settings">
		<div class="group">
			<div class="group-label">
				<h2 class="fs-18 lh-20 m0">Location</h2>
				<p class="mt-8 fs-14 lh-22">Enter your default ZIP code. This will also update your profile.</p>
			</div>
			<div>
				<PostalCodeField v-model="withinZip" no-label :country="$activeUser.profile.country" style="width:190px;" required>
					<template #unit>{{ $activeUser.profile.country == 'USA' ? 'ZIP' : 'Postal Code' }}</template>
				</PostalCodeField>
			</div>
		</div>

		<div class="group">
			<div class="group-label">
				<h2 class="fs-18 lh-20 m0">Available Roles</h2>
				<p class="mt-8 fs-14 lh-22">Select the roles for which you'd like to receive job offers.</p>
			</div>

			<div class="flex wrap" style="gap:10px;">
				<div v-for="role in availableRoles" class="role-checkbox checkbox-wrap" :key="`role${role.id}`">
					<input :id="`role${role.id}`" type="checkbox" v-model="selectedRoles" :value="role.id" />
					<label :for="`role${role.id}`">
						<div class="box">
							<icon-check-slim />
						</div>
						{{ role.name }}
					</label>
				</div>
			</div>
		</div>

		<!-- Role settings -->
		<div class="group" v-for="(roleSettings, i) in selectedRolesSettings" :key="`roleSettings${i}`">

			<div class="group-label">
				<h2 class="fs-18 lh-20 m0">{{ getRoleFromId(roleSettings.roleId).name }} Settings</h2>
				<p class="mt-8 fs-14 lh-22">
					Set your default settings for your {{ getRoleFromId(roleSettings.roleId).name }} role.
					<span class="rel" style="vertical-align: sub;">
						<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
							<path stroke="#004ADB" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12Z" />
							<path fill="#140047" d="M7.5 7.5H8V11h.5" />
							<path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7.5 7.5H8V11h.5" />
							<path fill="#140047" d="M8 6a.75.75 0 1 0 0-1.5A.75.75 0 0 0 8 6Z" />
						</svg>
						<Tooltip arrow="bottom center">You can change these settings in the future or customize individual dates at a later point.</Tooltip>
					</span>
				</p>
			</div>

			<div class="grid gap-32">
				<!-- Rate -->
				<div>
					<label class="fw-500 fs-14 color-violet mb-8 gap-4 flex" for="">
						Default {{ getRoleFromId(roleSettings.roleId).name }} Rate
						<span class="rel">
							<svg class="block" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
								<path stroke="#004ADB" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12Z" />
								<path fill="#140047" d="M7.5 7.5H8V11h.5" />
								<path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7.5 7.5H8V11h.5" />
								<path fill="#140047" d="M8 6a.75.75 0 1 0 0-1.5A.75.75 0 0 0 8 6Z" />
							</svg>
							<Tooltip arrow="bottom center">You can customize individual dates at a later point. The rate you put here is your default rate for normal dates.</Tooltip>
						</span>
					</label>
					<div class="flex gap-16">
						<NumberField v-model="roleSettings.ratePerHour" type="USD" min="0" :disabled="isLoading" required style="width:150px;">
						</NumberField>
						<span class="fs-14 fw-500 color-violet">per hour</span>
					</div>
					<p v-if="roleSettings.roleId == 2 && (roleSettings.ratePerHour > 35 || roleSettings.ratePerHour < 15)" class="fs-12 lh-14">
						The industry standard rate for Assistants is $15-$35/hour based on experience.
					</p>
					<p v-if="roleSettings.roleId == 3 && (roleSettings.ratePerHour > 70 || roleSettings.ratePerHour < 35)" class="fs-12 lh-14">
						The industry standard rate for Second Shooters is $35-$70/hour based on experience.
					</p>
					<p v-if="roleSettings.roleId == 4 && (roleSettings.ratePerHour > 130 || roleSettings.ratePerHour < 75)" class="fs-12 lh-14">
						The industry standard rate for Lead Shooters is $75-$130/hour based on experience.
					</p>
				</div>

				<!-- Radius -->
				<div>
					<label class="fw-500 fs-14 color-violet mb-8 gap-4 flex" for="">
						Default Availability Within
						<span class="rel">
							<svg class="block" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
								<path stroke="#004ADB" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12Z" />
								<path fill="#140047" d="M7.5 7.5H8V11h.5" />
								<path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7.5 7.5H8V11h.5" />
								<path fill="#140047" d="M8 6a.75.75 0 1 0 0-1.5A.75.75 0 0 0 8 6Z" />
							</svg>
							<Tooltip arrow="bottom center">You can customize individual dates at a later point. The radius you put here will apply to the zip code you used when signing up. Remember, studios do not pay travel pay, so set your radius small enough for travel pay to not be a determining factor.</Tooltip>
						</span>
					</label>
					<div class="radius-inputs flex gap-16">

						<DistanceField v-model="roleSettings.withinMiles" min="0" max="150" :disabled="isLoading" required style="max-width:190px;" />

						<div class="fs-14 color-violet">of {{ withinZip }}</div>
					</div>
				</div>

				<!-- Months out -->
				<div>
					<label class="fw-500 fs-14 color-violet mb-8 gap-4 flex" for="">
						I can be offered jobs up to...
						<span class="rel">
							<svg class="block" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
								<path stroke="#004ADB" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12Z" />
								<path fill="#140047" d="M7.5 7.5H8V11h.5" />
								<path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7.5 7.5H8V11h.5" />
								<path fill="#140047" d="M8 6a.75.75 0 1 0 0-1.5A.75.75 0 0 0 8 6Z" />
							</svg>
							<Tooltip arrow="bottom center">ShootWith.Me will automatically add availability for you a set number of months out so that you don't need to do it manually. You will still be able to accept or decline job offers when they come to you.</Tooltip>
						</span>
					</label>

					<div class="flex wrap gap-x-32 gap-y-16">
						<div class="range">
							<input type="range" v-model="roleSettings.monthsOut" min="3" max="24" id="" class="styled-slider slider-progress">
							<div class="range-markers">
								<div>3</div>
								<div style="left:18%">6</div>
								<div style="left:43%">12</div>
								<div style="left:69%">18</div>
								<div style="left:auto;right:0px">24</div>
							</div>
						</div>
						<NumberField v-model="roleSettings.monthsOut" min="0" max="24" :disabled="isLoading" required style="width:162px;" @change="onMonthsOutChange(roleSettings)">
							<template #unit>Months out</template>
						</NumberField>
					</div>
				</div>

				<!-- Days available -->
				<div>
					<label class="fw-500 fs-14 color-violet mb-8 gap-4 flex" for="">
						I'm usually available on...
						<span class="rel">
							<svg class="block" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
								<path stroke="#004ADB" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12Z" />
								<path fill="#140047" d="M7.5 7.5H8V11h.5" />
								<path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7.5 7.5H8V11h.5" />
								<path fill="#140047" d="M8 6a.75.75 0 1 0 0-1.5A.75.75 0 0 0 8 6Z" />
							</svg>
							<Tooltip arrow="bottom center">You will only have availability added for the days of the week you select here. If you want to get offers for every day of the week, you'll need to select all dates. If you're only available weekends, only select weekend days. You will still be able to decline job offers that aren't a good fit.</Tooltip>
						</span>
					</label>

					<div class="days-of-week">
						<div v-for="day in daysOfWeek" :key="`weekday-${day.value}`" :class="{'checked': roleSettings.daysOfWeek.includes(day.value)}" class="day">
							<input
								type="checkbox"
								name="daysOfWeek"
								:id="`${roleSettings.roleId}-daysOfWeek-${day.value}`"
								:value="day.value"
								v-model="roleSettings.daysOfWeek"
								:aria-label="day.longName"
								:disabled="isLoading || (roleSettings.daysOfWeek.length == 1 && roleSettings.daysOfWeek.includes(day.value))"
								/>
								<!-- Disable if only date selected -->
							<label :for="`${roleSettings.roleId}-daysOfWeek-${day.value}`">
								{{ day.shortName }}
								<svg class="ml-4" xmlns="http://www.w3.org/2000/svg" width="8" height="8" fill="none">
									<path stroke="#fff" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.25" d="M1 4.763 2.783 6.5l3.931-5" /></svg>
							</label>
						</div>
					</div>
				</div>

			</div>
		</div>

		<!-- todo: make global component? -->
		<div v-if="errorMessage || successMessage" :class="{'error-message': errorMessage, 'success-message': successMessage}" class="mt-24">
			{{ errorMessage ? errorMessage : successMessage }}
		</div>

		<div v-if="context == 'onboarding'" class="mt-24 flex-right gap-16">
			<button v-if="!hasCompletedOnboarding" class="btn btn-large align-left" @click="$emit('back')" :disabled="isProcessing">Back</button>
			<button :disabled="!canSave" :class="{'is-processing': isProcessing }" class="btn btn-green btn-large" @click="onSave">
				Publish Profile + Availability
			</button>
		</div>
		<div v-else class="mt-24 flex-right gap-16">
			<button class="btn btn-large" @click="$emit('close')" :disabled="isProcessing">Cancel</button>
			<button :disabled="!canSave" :class="{'is-processing': isProcessing }" class="btn btn-green btn-large" @click="onSave">
				Save Options
			</button>
		</div>
	</div>
</template>
  
<script>
import helpers from '@/mixins/helpers';
import formHelpers from '@/mixins/formHelpers';
import { request } from '@/plugins/axios';
import { Info } from 'luxon';

export default {
	name: 'AvailabilitySettings',
	mixins: [helpers, formHelpers],
	props: {
		context: [String, null],
	},
	data() {
		return {
			isLoading: false,
			selectedRoles: [],
			settings: [],
			withinZip: '',
		};
	},
	computed: {
		canSave() {
			return this.selectedRolesSettings.length;
		},
		hasCompletedOnboarding() {
			return this.$activeUser.profile.onboardingCompletedAt;
		},
		selectedRolesSettings() {
			const out = this.settings.filter(obj => this.selectedRoles.includes(obj.roleId));

			return out;
		},
		daysOfWeek() {
			const days = [2,3,4,5,6,7,1];
			const out = [];

			days.forEach(day => {
				const luxonDay = (day + 5) % 7;
				out.push({
					longName: Info.weekdays('long')[luxonDay],
					shortName: Info.weekdays('short')[luxonDay],
					value: day,
				})
			});
			
			return out;
		},
	},
	created() {
		if ( this.context == 'onboarding') {
			this.initOnboarding();
		} else {
			this.init();
		}
	},
	methods: {
		onMonthsOutChange(settings) {
			if (settings.monthsOut > 24) {
				settings.monthsOut = 24;
			} else if (settings.monthsOut < 3) {
				settings.monthsOut = 3;
			}
		},
		populateDefaults() {
			const myZip = this.$activeUser.profile.zip;
			this.withinZip = myZip;

			this.settings.push({
				roleId: 4,
				ratePerHour: 75,
				withinMiles: 100,
				monthsOut: 18,
				daysOfWeek: [1,2,3,4,5,6,7]
			});

			this.settings.push({
				roleId: 3,
				ratePerHour: 45,
				withinMiles: 100,
				monthsOut: 12,
				daysOfWeek: [1,2,3,4,5,6,7]
			});

			this.settings.push({
				roleId: 2,
				ratePerHour: 15,
				withinMiles: 100,
				monthsOut: 6,
				daysOfWeek: [1,2,3,4,5,6,7]
			});
		},
		init() {
			// First load default settings
			this.isLoading = true;
			this.populateDefaults();
			
			// Then get saved settings from store
			// These are fetched/stored in initActiveUser() in auth/actions.js
			const savedSettings = this.$store.getters['availability/defaultAvailability'];

			// Replace defaults + mark as selected for each
			savedSettings.list.forEach(obj => {
				let index = this.settings.findIndex(d => d.roleId == obj.roleId);
				this.settings[index] = obj;
				this.selectedRoles.push(obj.roleId);
			});

			this.withinZip = savedSettings.withinZip;
			this.isLoading = false;
		},
		initOnboarding() {
			this.populateDefaults();
			const myRoleId = this.$activeUser.role.roleId;
			
			// Select my role
			this.selectedRoles.push(myRoleId); 
			
			// If lead, also select 'second shooter'
			if (myRoleId == 4) {
				this.selectedRoles.push(3);
			}
		},
		onSave() {
			if (this.isProcessing) return;
			this.isProcessing = true;
			this.errorMessage = null;
			const payload = {
				list: this.selectedRolesSettings,
				withinZip: this.withinZip,
			};

			request({
				url: '/availabilities/default/me',
				method: 'put',
				data: payload,
				timeout: 60000,
			}).then(() => {
				this.$store.commit('availability/setDefaultAvailability', payload);
				this.refreshAvailability();
				this.onSuccess();
			}).catch(error => {
				this.isProcessing = false;
				this.errorMessage = this.getErrorMessage(error);
			});
		},
		onSuccess() {
			this.isProcessing = false;

			if (this.context == 'onboarding') {
				if (!this.hasCompletedOnboarding) {
					this.$store.commit('users/setHasSetDefaultAvailability', true);
					this.$emit('next');
				} else {
					this.$emit('next');
				}
			} else {
				this.flashSuccess('Your default availability settings have been saved.', 1000, 'close');
			}
		},
	}
};
</script>
